/* ==========================================================================
   custom-header-scroll.js — Sothiares
   --------------------------------------------------------------------------
   Hide-on-scroll-down, reveal-on-scroll-up for the sticky nav row.

   The reference (loropiana.com) keeps a fixed header and translates it up by
   exactly its own height when you scroll away from the top, sliding it back
   on the way up. Ours was position:relative and simply scrolled off, so the
   stylesheet makes the nav row sticky and this file drives the slide.

   Publishes --so-header-h so other sticky elements — notably the PDP tab bar —
   can offset themselves by the header's *visible* height and neither overlap
   it nor leave a gap when it hides.

   Author: Custom development team
   Created: 2026-09-04
   ========================================================================== */

(function () {
  'use strict';

  // How far down the page the header stays pinned before it may hide. This was
  // 120px, which on a ~100px header meant it sat over the top of the hero image
  // through exactly the range where people first scroll. Just past the
  // announcement bar is enough to avoid flicker at rest.
  var HIDE_AFTER = 40;
  // Movement below this is treated as jitter. Lower is more responsive; too low
  // and a trackpad's inertia can flip the header back and forth.
  var DELTA = 4;

  function init() {
    // The whole header group is the sticky unit, so the announcement bar and
    // the nav travel together rather than detaching — the stylesheet gives
    // #header-group a box, since the theme ships it as display:contents.
    var header = document.getElementById('header-group');
    // The nav row alone, measured separately: the homepage pulls its hero up
    // by this so the announcement bar keeps its own background.
    var navRow = document.querySelector('header.shopify-section-group-header-group');
    if (!header || !navRow) return;

    // The theme editor re-fires section:load on every save; without this guard
    // each one would stack another scroll listener on the same element.
    if (header.hasAttribute('data-so-header-ready')) return;
    header.setAttribute('data-so-header-ready', '');

    var root = document.documentElement;
    var lastY = window.scrollY;
    var hidden = false;
    var ticking = false;

    function publishHeight() {
      // Two values, deliberately different:
      //   --so-header-h       visible height, zero while hidden. For `top`
      //                       offsets, so sticky things sit flush when the
      //                       header slides away.
      //   --so-header-static  the header's real height, never zero. For
      //                       SIZING, so elements do not resize as the header
      //                       shows and hides.
      // Visible height of the whole sticky unit, zero while hidden.
      root.style.setProperty('--so-header-h', (hidden ? 0 : header.offsetHeight) + 'px');
      // Nav row only, never zero — sizing and the homepage hero offset use this.
      root.style.setProperty('--so-header-static', navRow.offsetHeight + 'px');

      // Full height of the header group including the announcement bar. The
      // homepage pulls its hero up by this so the header overlays the video.
      var group = 0;
      document.querySelectorAll('.shopify-section-group-header-group').forEach(function (s) {
        group += s.offsetHeight;
      });
      root.style.setProperty('--so-headergroup-h', group + 'px');
    }

    function setHidden(next) {
      if (next === hidden) return;
      hidden = next;
      header.classList.toggle('so-header--hidden', hidden);
      publishHeight();
    }

    function update() {
      ticking = false;
      var y = window.scrollY;

      // Always show at the very top, whatever the direction.
      if (y <= HIDE_AFTER) {
        setHidden(false);
        lastY = y;
        return;
      }

      var moved = y - lastY;
      if (Math.abs(moved) < DELTA) return;

      setHidden(moved > 0);
      lastY = y;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    window.addEventListener('resize', publishHeight, { passive: true });
    publishHeight();

    // ---- Homepage: header sits transparent over the video hero -------------
    // Reads the template off #MainContent rather than sniffing the URL, so it
    // stays correct on every market and locale prefix.
    var main = document.getElementById('MainContent');
    var isHome = main && (main.dataset.template || '').indexOf('index') === 0;
    if (!isHome) return;

    var hero = main.firstElementChild;
    if (!hero) return;

    root.classList.add('so-home-hero');

    var overHero = false;
    var pointerOn = false;

    function paintOverlay() {
      // Solid whenever the pointer is on the header, or once the hero has
      // scrolled past — transparent only while genuinely over the video.
      var on = overHero && !pointerOn;
      header.classList.toggle('so-header--over-hero', on);
      // Never slide the header away while it is part of the hero.
      if (on) setHidden(false);
    }

    function measureHero() {
      var limit = hero.offsetHeight - header.offsetHeight;
      var next = window.scrollY < Math.max(limit, 0);
      if (next !== overHero) {
        overHero = next;
        paintOverlay();
      }
    }

    header.addEventListener('pointerenter', function () {
      pointerOn = true;
      paintOverlay();
    });
    header.addEventListener('pointerleave', function () {
      pointerOn = false;
      paintOverlay();
    });

    window.addEventListener('scroll', measureHero, { passive: true });
    window.addEventListener('resize', measureHero, { passive: true });
    overHero = true;
    paintOverlay();
    measureHero();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
